import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs'

import { ApiInvoiceService } from '@modules/private/invoices/data/services/api/api-invoice.service'
import { CONCEPT_LABEL } from '@modules/private/invoices/data/constants/conceptos'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Qué hay que hacer con esta factura. Una sola respuesta, no cuatro columnas de pastillas. */
export type SituacionFactura = 'sinArchivo' | 'porCobrar' | 'faltaRep' | 'cerrada' | 'cancelada'

/** Los filtros de la barra. `todas` no manda nada al servidor. */
export interface ChipSituacion {
  key: SituacionFactura | 'todas'
  label: string
  tono: 'neutro' | 'warn' | 'bad' | 'ok'
  /** El filtro que se manda. Vacío = sin filtrar. */
  filtro: Record<string, { value: unknown; matchMode: string }>
}

const SITUACIONES: Record<SituacionFactura, { label: string; tono: string; ayuda: string }> = {
  sinArchivo: { label: 'Sin archivo', tono: 'warn', ayuda: 'La referencia existe pero no se han subido su PDF y su XML' },
  porCobrar: { label: 'Por cobrar', tono: 'info', ayuda: 'CFDI emitido y todavía sin pago conciliado' },
  faltaRep: { label: 'Falta REP', tono: 'warn', ayuda: 'Es PPD, ya está pagada y le falta el complemento de pago' },
  cerrada: { label: 'Cerrada', tono: 'ok', ayuda: 'Pagada y sin nada pendiente' },
  cancelada: { label: 'Cancelada', tono: 'bad', ayuda: 'Cancelada o refacturada' },
}

/**
 * Los filtros de estado, como fichas.
 *
 * Eran cuatro dropdowns «Cualquiera» sobre las cabeceras —Estatus, Método pago, Estatus de pago y
 * Complemento— y para preguntar «¿qué me falta cobrar?» había que saber en cuál de los cuatro estaba
 * la respuesta. Aquí cada ficha es una pregunta de trabajo, no una columna de la base.
 */
const CHIPS: ChipSituacion[] = [
  { key: 'todas', label: 'Todas', tono: 'neutro', filtro: {} },
  { key: 'sinArchivo', label: 'Sin archivo', tono: 'warn', filtro: { status: { value: 'PENDING_FILES', matchMode: 'equals' } } },
  { key: 'porCobrar', label: 'Por cobrar', tono: 'neutro', filtro: { paymentStatus: { value: 'PENDING', matchMode: 'equals' } } },
  //* El complemento pendiente solo tiene sentido en PPD pagadas: el mismo criterio que usa el aviso
  //* del encabezado y el cron que le escribe a Operaciones.
  { key: 'faltaRep', label: 'Falta REP', tono: 'warn', filtro: { complementStatus: { value: 'PENDING', matchMode: 'equals' }, paymentType: { value: 'PPD', matchMode: 'equals' } } },
  { key: 'cerrada', label: 'Cerradas', tono: 'ok', filtro: { paymentStatus: { value: 'PAID', matchMode: 'equals' } } },
  { key: 'cancelada', label: 'Canceladas', tono: 'bad', filtro: { status: { value: ['CANCELLED', 'REPLACED'], matchMode: 'in' } } },
]

/** Una fila, ya resuelta. */
export interface FilaFactura {
  raw: any
  _id: string
  folioFiscal: string
  uid: string | null
  leaseId: string | null
  leaseFolio: string
  concepto: string
  mes: string | null
  monto: number | null
  situacion: SituacionFactura
  metodo: string | null
  fechaCfdi: string | null
  fechaPago: string | null
}

/**
 * La tabla de Comprobantes: qué se facturó y **qué falta hacer con cada factura**.
 *
 * ## Qué cambia frente a la genérica
 *
 * Tenía **doce columnas** y seis filtros, cuatro de ellos dropdowns «Cualquiera» descuadrados sobre
 * las cabeceras. Cuatro de esas columnas eran estado —Estatus, Método pago, Estatus de pago y
 * Complemento— y ninguna contestaba sola la pregunta de Operaciones, que es siempre la misma:
 * **¿qué me falta hacer?**. Había que leer las cuatro y combinarlas mentalmente.
 *
 * Aquí esas cuatro se funden en una **situación**: sin archivo · por cobrar · falta REP · cerrada ·
 * cancelada. Es la misma información, resuelta. Lo que se pierde de vista —el método, las fechas, el
 * UUID completo— vive en el desplegable, que es donde se mira cuando de verdad hace falta.
 *
 * Un buscador para los tres identificadores (folio fiscal, UUID, arrendamiento) y fichas para la
 * situación. La tabla genérica **no se toca**: la usan muchas pantallas.
 */
@Component({
  selector: 'app-invoices-table',
  templateUrl: './invoices-table.component.html',
  styleUrls: ['./invoices-table.component.scss'],
})
export class InvoicesTableComponent implements OnInit, OnDestroy {
  /** El mes que enseña la pantalla. Se usa solo para el texto del vacío, no para filtrar. */
  @Input() monthLabel = ''

  /** Una acción sobre una fila. Los diálogos viven en la vista de Comprobantes. */
  @Output() accion = new EventEmitter<{ action: string; data: any }>()

  filas: FilaFactura[] = []
  total = 0
  cargando = true

  pagina = 0
  //* 30 · 60 · 90: los tamaños con los que Operaciones revisa un corte. El default es el más chico
  //* porque la tabla se lee de arriba abajo y treinta filas ya llenan su ventana.
  porPagina = 30
  readonly opcionesPagina = [30, 60, 90]

  busqueda = ''
  chip: ChipSituacion['key'] = 'todas'
  abiertas = new Set<string>()

  readonly chips = CHIPS
  readonly situaciones = SITUACIONES
  readonly esqueleto = [1, 2, 3, 4, 5, 6]

  private readonly buscar$ = new Subject<string>()
  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiInvoiceService,
    private readonly auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.buscar$.pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.pagina = 0
      this.cargar()
    })
    this.cargar()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onBuscar(valor: string): void {
    this.busqueda = valor
    this.buscar$.next(valor)
  }

  limpiarBusqueda(): void {
    if (!this.busqueda) return
    this.onBuscar('')
  }

  verChip(c: ChipSituacion): void {
    if (this.chip === c.key) return
    this.chip = c.key
    this.pagina = 0
    this.cargar()
  }

  onPagina(e: { first?: number; rows?: number }): void {
    this.porPagina = e.rows ?? this.porPagina
    this.pagina = this.porPagina ? Math.floor((e.first ?? 0) / this.porPagina) : 0
    this.cargar()
  }

  /** Recarga sin perder búsqueda ni ficha. La usa la vista tras cargar archivos o cancelar. */
  recargar(): void {
    this.cargar()
  }

  estaAbierta(f: FilaFactura): boolean {
    return this.abiertas.has(f._id)
  }

  alternar(f: FilaFactura): void {
    this.abiertas.has(f._id) ? this.abiertas.delete(f._id) : this.abiertas.add(f._id)
  }

  conceptoLabel(concepto: string): string {
    return CONCEPT_LABEL[concepto] || concepto || '—'
  }

  /**
   * Qué se puede hacer con esta factura, ya filtrado por su estado.
   *
   * Cada acción conserva **su color**, el mismo que tenía en la tabla genérica: rojo el PDF y el
   * cancelar, oscuro el XML, ámbar el refacturar. Sin severidad todos salían del mismo gris y había
   * que leer el tooltip para distinguir «descargar» de «cancelar» — dos acciones que no se parecen
   * en nada.
   */
  acciones(f: FilaFactura): { action: string; label: string; icon: string; tono: string }[] {
    const raw = f.raw
    const out: { action: string; label: string; icon: string; tono: string }[] = []
    if (raw?.status === 'PENDING_FILES') out.push({ action: 'CARGAR', label: 'Cargar archivos', icon: 'fa-solid fa-cloud-arrow-up', tono: 'info' })
    //* Solo «Sin archivo»: con el CFDI ya ligado el folio lo dice el XML y se corrige con Refacturar.
    if (raw?.status === 'PENDING_FILES' && this.auth.hasModulePermission('invoices', 'update')) out.push({ action: 'EDITAR', label: 'Editar referencia', icon: 'fa-solid fa-pen', tono: 'dark' })
    if (raw?.pdfUrl) out.push({ action: 'DOWNLOAD_PDF', label: 'PDF', icon: 'fa-solid fa-file-pdf', tono: 'danger' })
    if (raw?.xmlUrl) out.push({ action: 'DOWNLOAD_XML', label: 'XML', icon: 'fa-solid fa-file-code', tono: 'dark' })
    if (raw?.paymentType === 'PPD') out.push({ action: 'COMPLEMENTOS', label: 'Complementos', icon: 'fa-solid fa-receipt', tono: 'help' })
    if (raw?.status === 'LINKED') {
      out.push({ action: 'REFACTURAR', label: 'Refacturar', icon: 'fa-solid fa-arrows-rotate', tono: 'warning' })
      out.push({ action: 'CANCELAR', label: 'Cancelar', icon: 'fa-solid fa-ban', tono: 'danger' })
    }
    if (raw?.active === false) out.push({ action: 'HISTORY', label: 'Historial', icon: 'fa-solid fa-clock-rotate-left', tono: 'secondary' })

    return out
  }

  private cargar(): void {
    this.cargando = true
    const chip = CHIPS.find((c) => c.key === this.chip) ?? CHIPS[0]
    const filtros: Record<string, unknown> = { ...chip.filtro }
    if (this.busqueda.trim()) filtros['q'] = { value: this.busqueda.trim(), matchMode: 'contains' }

    this.api.getAll(this.pagina + 1, this.porPagina, filtros).subscribe((r: any) => {
      this.cargando = false
      if (r.error) {
        this.filas = []
        this.total = 0
        return
      }
      this.total = r.data?.totalItems ?? 0
      this.filas = (r.data?.data ?? []).map((i: any) => this.aFila(i))
      this.abiertas.clear()
    })
  }

  private aFila(i: any): FilaFactura {
    return {
      raw: i,
      _id: String(i?._id ?? ''),
      folioFiscal: i?.folioFiscal ?? '—',
      uid: i?.uid ?? null,
      leaseId: i?.lease ? String(i.lease?._id ?? i.lease) : null,
      leaseFolio: i?.leaseFolio ?? '—',
      concepto: i?.concept ?? '',
      mes: i?.month ?? null,
      monto: typeof i?.amount === 'number' ? i.amount : null,
      situacion: this.situacionDe(i),
      metodo: i?.paymentType ?? null,
      fechaCfdi: i?.issueDate ?? null,
      fechaPago: i?.paidAt ?? null,
    }
  }

  /**
   * Las cuatro dimensiones de estado, resueltas en una.
   *
   * El orden importa y no es arbitrario: **primero lo que impide seguir**. Una factura cancelada ya
   * no pide nada; una sin archivo no puede cobrarse; una PPD pagada sin REP está incompleta ante el
   * SAT aunque el dinero ya entró. Lo demás es «cerrada».
   */
  private situacionDe(i: any): SituacionFactura {
    if (i?.status === 'CANCELLED' || i?.status === 'REPLACED' || i?.active === false) return 'cancelada'
    if (i?.status === 'PENDING_FILES') return 'sinArchivo'
    if (i?.paymentType === 'PPD' && i?.paymentStatus === 'PAID' && i?.complementStatus === 'PENDING') return 'faltaRep'
    if (i?.paymentStatus !== 'PAID') return 'porCobrar'

    return 'cerrada'
  }
}
